import React, { useCallback, useEffect } from 'react';
import { When, If, Then, Else } from 'react-if';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import './styles.css';
import { LogoIcon, LogOutIcon } from '../../elements/icons/Icons';
import { ReactComponent as SidekickLogo } from './sidekick-studios-logo.svg';
import { ReactComponent as CreateTeamsIcon } from './create-teams-icons.svg';
import { ReactComponent as AccountSettingsIcon } from './account-settings-icon.svg';
import { ReactComponent as InviteFriendsIcon } from './invite-friends.svg';
import { useToggle } from 'react-use';
import AccountSettingsModal from '../../elements/customModals/AccountSettingsModal/AccountSettingsModal';
import MyTeamsModal from '../../elements/customModals/MyTeamsModal/MyTeamsModal';
import InviteFriendsModal from '../../elements/modal/InviteFriendsModal';
import { useAuth } from '../../../hooks/useAuth';
import { doSignOut } from '../../../firebase/api/auth';
import * as ROUTES from '../../../constants/routes';
import classNames from 'classnames';
import { ReactComponent as Delimitr } from './delimetr.svg';
import { IS_SIDEKICK_APP, IS_VALIDATE_APP } from '../../../constants/projects';

const MainLayout = ({ children, menuRenderer, isPreview }) => {
    const {
        currentUser: { inviteCode },
    } = useAuth();
    const history = useHistory();

    const [isCreateTeamPopupOpens, toggleCreateTeamPopup] = useToggle(false);
    const [isMyTeamPopupOpens, toggleMyTeamPopup] = useToggle(false);
    const [isInviteFriendsPopupOpens, toggleInviteFriendsPopup] = useToggle(false);

    useEffect(() => {
        if (window.location.pathname.includes(ROUTES.PROFILE)) {
            toggleCreateTeamPopup();
        }
    }, [toggleCreateTeamPopup]);

    const handleDashboardButtonClick = useCallback(() => {
        window.location.reload();
    }, []);

    const handleSignOutButtonClick = useCallback(async () => {
        await doSignOut();
        history.push(ROUTES.LANDING);
    }, [history]);

    return (
        <div className="main-layout__container">
            <When condition={isPreview}>
                <div className="main-layout__preview" />
            </When>
            <div
                className={classNames('main-layout__left-nav', {
                    'main-layout__left-nav--sidekick': IS_SIDEKICK_APP,
                })}
            >
                <div
                    className={classNames({
                        'main-layout__logo': IS_VALIDATE_APP,
                        'main-layout__logo--sidekick my-4': IS_SIDEKICK_APP,
                    })}
                >
                    <If condition={IS_SIDEKICK_APP}>
                        <Then>
                            <SidekickLogo />
                        </Then>
                        <Else>
                            <LogoIcon />
                        </Else>
                    </If>
                </div>
                {menuRenderer()}
                <div className="my-4">
                    <Delimitr />
                </div>
                <p className="main-layout__nav-link nav-headline">MENU</p>
                <div
                    className={classNames('btn btn-link main-layout__nav-link  mb-2', {
                        'main-layout__nav-link--active': isMyTeamPopupOpens === true,
                    })}
                    onClick={toggleMyTeamPopup}
                >
                    <div className="main-layout__nav-icon">
                        <CreateTeamsIcon />
                    </div>
                    <p>Create Teams</p>
                </div>
                <div
                    className={classNames('btn btn-link main-layout__nav-link  mb-2', {
                        'main-layout__nav-link--active': isCreateTeamPopupOpens === true,
                    })}
                    onClick={toggleCreateTeamPopup}
                >
                    <div className="main-layout__nav-icon">
                        <AccountSettingsIcon />
                    </div>
                    <p>Account Settings</p>
                </div>
                <div
                    className={classNames('btn btn-link main-layout__nav-link  mb-2', {
                        'main-layout__nav-link--active': isInviteFriendsPopupOpens === true,
                    })}
                    onClick={toggleInviteFriendsPopup}
                >
                    <div className="main-layout__nav-icon">
                        <InviteFriendsIcon />
                    </div>
                    <If condition={IS_SIDEKICK_APP}>
                        <Then>
                            <p>Invite talent</p>
                        </Then>
                        <Else>
                            <p>Invite friends</p>
                        </Else>
                    </If>
                </div>
                <div className="my-4">
                    <Delimitr />
                </div>
                <div
                    className="btn btn-link main-layout__nav-link"
                    onClick={handleSignOutButtonClick}
                >
                    <div className="main-layout__nav-icon">
                        <LogOutIcon />
                    </div>
                    <p>Log out</p>
                </div>
            </div>
            {children}
            <When condition={isCreateTeamPopupOpens}>
                {() => (
                    <AccountSettingsModal
                        modal={isCreateTeamPopupOpens}
                        toggle={toggleCreateTeamPopup}
                    />
                )}
            </When>
            <When condition={isMyTeamPopupOpens}>
                {() => <MyTeamsModal modal={isMyTeamPopupOpens} toggle={toggleMyTeamPopup} />}
            </When>
            <When condition={isInviteFriendsPopupOpens}>
                {() => (
                    <InviteFriendsModal
                        inviteCode={inviteCode}
                        modal={isInviteFriendsPopupOpens}
                        toggle={toggleInviteFriendsPopup}
                    />
                )}
            </When>
        </div>
    );
};

MainLayout.propTypes = {
    menuRenderer: PropTypes.func.isRequired,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
    isPreview: PropTypes.bool,
};

MainLayout.defaultProps = {
    isPreview: false,
};

export default React.memo(MainLayout);
